import Pagination, { PagingData } from '../Pagination/Pagination';
import { CorporationListItem } from '@/api/corporation/type';
import styles from './SelectList.module.css';
import { useState, useEffect } from 'react';

interface ClickListProps<CorporationListItem> {
    title: string;
    data?: CorporationListItem[];
    paging?: PagingData;
    onPageChange?: (page: number) => void;
    onClickRow?: (row: CorporationListItem) => void;
    initialActiveIdx?: number; // 초기 active 상태 (선택사항)
}

const SelectList = ({
    title,
    data,
    paging,
    onPageChange,
    onClickRow,
    initialActiveIdx,
}: ClickListProps<CorporationListItem>) => {
    // 내부 상태로 activeIdx 관리
    const [activeIdx, setActiveIdx] = useState<number | undefined>(
        initialActiveIdx,
    );

    // data가 변경될 때마다 초기값 설정 또는 첫 번째 row 자동 선택
    useEffect(() => {
        if (data && data.length > 0) {
            let targetRow: CorporationListItem | undefined;
            let targetIdx: number | undefined;

            // initialActiveIdx가 있으면 해당 항목을 찾기
            if (initialActiveIdx !== undefined) {
                targetRow = data.find(row => row.userIdx === initialActiveIdx);
                if (targetRow) {
                    targetIdx = initialActiveIdx;
                }
            }

            // initialActiveIdx에 해당하는 항목이 없거나 initialActiveIdx가 없으면 첫 번째 항목 선택
            if (!targetRow) {
                targetRow = data[0];
                targetIdx = targetRow.userIdx;
            }

            setActiveIdx(targetIdx);
            handleRowClick(targetRow);
        }
    }, [data, initialActiveIdx]);

    // 행 클릭 핸들러
    const handleRowClick = (row: CorporationListItem) => {
        setActiveIdx(row.userIdx);

        // 외부 onClickRow 콜백이 있다면 실행
        if (onClickRow) {
            onClickRow(row);
        }
    };

    // 페이지 변경 핸들러
    const handlePageChange = (page: number) => {
        // 외부 onPageChange 콜백 실행
        if (onPageChange) {
            onPageChange(page);
        }
    };

    return (
        <div className="card border-0 mb-0">
            {/* 헤더 */}
            <div className="card-title border-bottom mt-3 pb-1 text-gray">
                <span>
                    <i className="ri-check-line"></i> {title}
                </span>
            </div>

            {/* 리스트 */}
            <ul
                className={`list-unstyled browser-statistics-list select-list mt-3 ${styles.selectList}`}
            >
                {data?.length === 0 ? (
                    <li>
                        <div className="p-2 border border-primary border-dashed border-opacity-25 rounded text-center">
                            데이터가 존재하지않습니다.
                        </div>
                    </li>
                ) : (
                    data?.map((row, rowIndex) => (
                        <li
                            key={rowIndex}
                            className={`${activeIdx === row.userIdx ? styles.active : ''} cursor-pointer`}
                            onClick={() => handleRowClick(row)}
                        >
                            <div className="d-flex align-items-center flex-wrap gap-3 p-2 border border-primary border-dashed border-opacity-25 rounded">
                                <div>
                                    <span
                                        className={`avatar avatar-md ${activeIdx === row.userIdx ? 'bg-success-transparent' : 'text-success'}`}
                                    >
                                        <i
                                            className={`${activeIdx === row.userIdx ? 'ri-checkbox-circle-line' : 'ri-checkbox-blank-circle-line'} fs-4`}
                                        ></i>
                                    </span>
                                </div>
                                <div className="flex-fill">
                                    <span className="fw-medium">
                                        대행사명 : {row.userName}
                                    </span>
                                    <span className="d-block text-muted fs-12">
                                        {row.userId}
                                        {/* | {row.phone}*/}
                                    </span>
                                </div>
                                <div className="text-end ms-auto">
                                    <span className="fw-semibold d-block mb-1">
                                        <span className="fw-normal fs-12">
                                            <i
                                                className={`ri-circle-fill fs-8 me-1 ${row.seasonCnt === 0 ? 'text-muted' : 'text-primary'} `}
                                            ></i>
                                            등록작기
                                        </span>{' '}
                                        {row.seasonCnt}
                                    </span>
                                    <span className="fs-11 fw-medium text-success d-block">
                                        <i className="ri-calendar-line"></i>{' '}
                                        {row.regDt}
                                    </span>
                                </div>
                            </div>
                        </li>
                    ))
                )}
            </ul>

            {/* 페이징 */}
            {paging && paging.totalPage > 1 ? (
                <Pagination paging={paging} onPageChange={handlePageChange} />
            ) : (
                <div className="pt-5"></div>
            )}
        </div>
    );
};

export default SelectList;
